import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Plane } from "lucide-react";
import { scrollToId } from "@/lib/api";
import { IMAGES } from "@/lib/images";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-center overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={IMAGES.heroArrival}
          alt="Downtown Atlanta skyline at night"
          className="h-[120%] w-full scale-105 object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-obsidian/80 via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-28 sm:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="mb-8 inline-flex items-center gap-3 rounded-full border border-gold/25 bg-black/40 px-5 py-2 backdrop-blur-md"
          data-testid="hero-arrival-badge"
        >
          <Plane size={14} className="text-gold" />
          <span className="font-mono text-[11px] tracking-[0.3em] text-gold/90 uppercase">
            Hartsfield–Jackson ATL → Downtown
          </span>
        </motion.div>

        <h1 className="font-serif text-5xl leading-[0.95] font-bold tracking-tight text-slate-100 sm:text-7xl lg:text-8xl">
          <MaskedLine delay={0.35}>TOUCHDOWN</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="gold-gradient-text italic">to Skyline.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 sm:text-lg"
        >
          Land in Atlanta and step straight into luxury. A blacked-out fleet of
          luxury trucks and exotics waiting at arrivals — and a 45th-floor
          penthouse above downtown, reserved in your name.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-fleet-btn"
            onClick={() => scrollToId("fleet")}
            className="rounded-full bg-gold px-8 py-4 font-display text-xs font-semibold tracking-[0.25em] text-obsidian uppercase transition-all hover:bg-gold-bright hover:shadow-[0_0_40px_rgba(212,175,55,0.4)]"
          >
            Explore the Fleet
          </button>
          <button
            data-testid="hero-book-penthouse-btn"
            onClick={() => scrollToId("condo")}
            className="rounded-full border border-gold/40 px-8 py-4 font-display text-xs font-semibold tracking-[0.25em] text-gold uppercase transition-all hover:border-gold hover:bg-gold/10"
          >
            The Penthouse
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-16 flex flex-wrap gap-x-10 gap-y-3 border-t border-gold/15 pt-6 font-mono text-[11px] tracking-[0.25em] text-zinc-500 uppercase"
          data-testid="hero-stats-strip"
        >
          <span>33.7537° N · 84.3863° W</span>
          <span>6-Vehicle Fleet</span>
          <span>45th-Floor Penthouse</span>
          <span>24/7 Concierge</span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.6, delay: 1.3, ease: EASE }}
        className="absolute bottom-0 left-0 z-10 h-[2px] w-full origin-left bg-gradient-to-r from-gold via-gold-bright to-transparent"
      />
    </section>
  );
};
